import React, { useEffect, useState } from 'react';
import {
  Box,
  ButtonBase,
  IconButton,
  SwipeableDrawer,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { Notification } from '../hooks/useNotifications';
import { useNotificationsContext } from '../contexts/NotificationsContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import Txt from './design-system/Txt';
import { INK, MONO, MonoLabel, PanelButton } from './instrument';
import { enablePushOnThisDevice } from '../utils/enablePush';
import { isCapacitorNative, checkNativePushPermission } from '../utils/capacitor';
import { webPushSupported } from '../utils/webPush';
import { isTWA } from '../utils/platform';

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
  } = useNotificationsContext();

  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [enablingNotifications, setEnablingNotifications] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');
  const [swipeStates, setSwipeStates] = useState<Record<string, number>>({});
  const [clearingAll, setClearingAll] = useState(false);

  useEffect(() => {
    if (open) {
      fetchNotifications(50, showUnreadOnly);
    }
  }, [open, showUnreadOnly, fetchNotifications]);

  const getNotificationColor = (notification: Notification) => {
    if (notification.type === 'coaching') return INK.cyan;
    if (notification.data?.type === 'friend_post') return '#CE93D8';
    if (notification.data?.type === 'friend_message') return '#CE93D8';
    if (notification.data?.type === 'friend_request') return '#00E0FF';
    if (notification.type === 'friend_activity') return '#00E0FF';
    return INK.muted;
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification._id);
    }
    if (notification.data?.url) {
      let url = notification.data.url;
      const postId = notification.data.postId;
      if (postId && !url.includes('postId=')) {
        url += `${url.includes('?') ? '&' : '?'}postId=${postId}`;
      }
      onClose();
      setTimeout(() => {
        navigate(url);
      }, 350);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    fetchNotifications(50, showUnreadOnly);
  };

  // Push for THIS device: native permission in the apps, a live web-push subscription in
  // a browser. The old row read only the browser permission, so it said "Off" in the
  // apps (and on any browser without one) even when pushes arrived (issue #17 item 12).
  // The row now shows only when push is really off here and can be turned on.
  const [devicePush, setDevicePush] = useState<'on' | 'off' | 'unknown'>('unknown');
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        if (isCapacitorNative()) {
          const st = await checkNativePushPermission();
          if (alive) setDevicePush(st === 'granted' ? 'on' : st === 'unsupported' ? 'unknown' : 'off');
          return;
        }
        if (!webPushSupported()) { if (alive) setDevicePush('unknown'); return; }
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        const permission = window.Notification.permission;
        const permitted = permission === 'granted' || isTWA();
        if (alive) setDevicePush(sub && permitted ? 'on' : permission === 'denied' && !isTWA() ? 'unknown' : 'off');
      } catch { if (alive) setDevicePush('unknown'); }
    })();
    return () => { alive = false; };
  }, [open]);

  const handleEnableNotifications = async () => {
    setEnablingNotifications(true);
    try {
      const result = await enablePushOnThisDevice();
      if (result === 'granted') {
        setDevicePush('on');
        setSnackbarMessage('Notifications are on for this device.');
        setSnackbarSeverity('success');
      } else if (result === 'denied') {
        setSnackbarMessage('Notifications are blocked. Turn them on in your device settings.');
        setSnackbarSeverity('error');
      } else {
        setSnackbarMessage('Could not turn on notifications. Try again.');
        setSnackbarSeverity('error');
      }
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('Could not turn on notifications. Try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setEnablingNotifications(false);
    }
  };

  const handleDeleteNotification = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const success = await deleteNotification(notificationId);
    if (success) {
      setSnackbarMessage('Notification deleted');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    }
  };

  const handleClearAll = async () => {
    setClearingAll(true);
    try {
      const success = await clearAllNotifications();
      if (success) {
        setSnackbarMessage('All notifications cleared');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Failed to clear notifications');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage('Error clearing notifications');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setClearingAll(false);
    }
  };

  // Swipe handlers for touch devices
  const handleTouchStart = (notificationId: string, e: React.TouchEvent) => {
    setSwipeStates(prev => ({ ...prev, [notificationId]: e.touches[0].clientX }));
  };

  const handleTouchMove = (notificationId: string, e: React.TouchEvent) => {
    const startX = swipeStates[notificationId];
    if (startX === undefined) return;

    const currentX = e.touches[0].clientX;
    const diff = startX - currentX;

    // Update visual feedback (could add translateX here for visual swipe)
    if (diff > 0) {
      setSwipeStates(prev => ({ ...prev, [`${notificationId}_offset`]: Math.min(diff, 100) }));
    }
  };

  const handleTouchEnd = async (notificationId: string) => {
    const offset = swipeStates[`${notificationId}_offset`] || 0;

    // If swiped more than 80px, delete the notification
    if (offset > 80) {
      await deleteNotification(notificationId);
      setSnackbarMessage('Notification deleted');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    }

    // Reset swipe state
    setSwipeStates(prev => {
      const newState = { ...prev };
      delete newState[notificationId];
      delete newState[`${notificationId}_offset`];
      return newState;
    });
  };

  const segSx = (on: boolean) => ({
    minHeight: 28, px: 1.25, borderRadius: '6px',
    fontFamily: MONO, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const,
    color: on ? INK.ground : INK.cyan,
    background: on ? INK.cyan : 'transparent',
    border: `1px solid ${on ? INK.cyan : 'rgba(0,224,255,0.35)'}`,
  });

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      // Above the fixed header (drawer + 1), so the drawer's own title and close button show
      sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
      PaperProps={{
        sx: {
          width: isMobile ? 'calc(100% - 48px)' : 400,
          maxWidth: 400,
          background: 'var(--bg-card)',
          borderLeft: '1px solid rgba(0,224,255,0.25)',
          '&&': { borderRadius: '0 !important' },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            minHeight: 52,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0,224,255,0.15)',
            background: `linear-gradient(180deg, ${INK.cyan}0A 0%, transparent 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon sx={{ fontSize: 20, color: INK.cyan, filter: `drop-shadow(0 0 6px ${INK.cyan}99)` }} />
            <Txt component="h2" sx={{ m: 0, fontFamily: MONO, fontWeight: 800, fontSize: '0.9rem', letterSpacing: 2, textTransform: 'uppercase', color: INK.text }}>
              Notifications
            </Txt>
            {unreadCount > 0 && (
              <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.7rem', fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '4px', color: INK.cyan, background: `${INK.cyan}1F` }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </Txt>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close notifications"
            sx={{
              color: INK.cyan,
              backgroundColor: 'rgba(0,224,255,0.1)',
              border: '1px solid rgba(0,224,255,0.2)',
              '&:hover': { backgroundColor: 'rgba(0,224,255,0.2)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Push is off on this device: one line and the button that turns it on. */}
        {devicePush === 'off' && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: `1px solid ${INK.faint}`,
            }}
          >
            <Txt sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem', lineHeight: 1.35, color: INK.soft }}>
              Notifications are off on this device.
            </Txt>
            <PanelButton variant="primary" onClick={handleEnableNotifications} disabled={enablingNotifications} sx={{ minHeight: 36, px: 1.25, flexShrink: 0 }}>
              {enablingNotifications ? 'Turning on…' : 'Turn on'}
            </PanelButton>
          </Box>
        )}

        {/* Actions */}
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            borderBottom: `1px solid ${INK.faint}`,
          }}
        >
          <ButtonBase onClick={() => setShowUnreadOnly(false)} sx={segSx(!showUnreadOnly)}>
            All
          </ButtonBase>
          <ButtonBase onClick={() => setShowUnreadOnly(true)} sx={segSx(showUnreadOnly)}>
            {unreadCount > 0 ? `Unread (${unreadCount})` : 'Unread'}
          </ButtonBase>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25 }}>
            {unreadCount > 0 && (
              <PanelButton variant="quiet" onClick={handleMarkAllRead} sx={{ minHeight: 28, px: 0.75, fontSize: '0.7rem' }}>
                Mark all read
              </PanelButton>
            )}
            {notifications.length > 0 && (
              <PanelButton variant="quiet" onClick={handleClearAll} disabled={clearingAll} sx={{ minHeight: 28, px: 0.75, fontSize: '0.7rem' }}>
                {clearingAll ? 'Clearing...' : 'Clear all'}
              </PanelButton>
            )}
          </Box>
        </Box>

        {/* Notification List */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
              <CircularProgress size={22} sx={{ color: INK.cyan }} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ px: 1.5, py: 2 }}>
              <MonoLabel color={INK.muted}>{showUnreadOnly ? 'All caught up' : 'Nothing here yet'}</MonoLabel>
              <Txt sx={{ mt: 0.5, fontSize: '0.8rem', color: INK.muted, lineHeight: 1.4 }}>
                {showUnreadOnly ? 'No unread notifications' : 'No notifications yet'}
              </Txt>
            </Box>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {notifications.map((notification) => {
                const dot = getNotificationColor(notification);
                return (
                  <Box
                    component="li"
                    key={notification._id}
                    onTouchStart={(e: React.TouchEvent) => handleTouchStart(notification._id, e)}
                    onTouchMove={(e: React.TouchEvent) => handleTouchMove(notification._id, e)}
                    onTouchEnd={() => handleTouchEnd(notification._id)}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'flex-start',
                      borderBottom: `1px solid ${INK.faint}`,
                      background: notification.read ? 'transparent' : 'rgba(0,224,255,0.04)',
                      transform: `translateX(-${swipeStates[`${notification._id}_offset`] || 0}px)`,
                      transition: swipeStates[notification._id] ? 'none' : 'transform 0.2s ease-out',
                    }}
                  >
                    <ButtonBase
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        flex: 1, minWidth: 0, minHeight: 48, px: 1.5, py: 1,
                        display: 'flex', alignItems: 'flex-start', gap: 1.25, textAlign: 'left',
                        '&:hover': { background: 'rgba(0,224,255,0.06)' },
                      }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          mt: '6px', width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                          background: notification.read ? 'transparent' : dot,
                          border: `1.5px solid ${dot}`,
                          boxShadow: notification.read ? 'none' : `0 0 8px ${dot}`,
                          opacity: notification.read ? 0.55 : 1,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                          <Txt component="span" sx={{
                            flex: 1, minWidth: 0, fontSize: '0.8rem', fontWeight: notification.read ? 500 : 700, lineHeight: 1.35,
                            color: notification.read ? INK.muted : INK.text,
                          }}>
                            {notification.title}
                          </Txt>
                          <Txt component="span" sx={{ fontFamily: MONO, fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {formatDistanceToNow(new Date(notification.sentAt), { addSuffix: true })}
                          </Txt>
                        </Box>
                        {notification.body && (
                          <Txt sx={{ mt: 0.25, fontSize: '0.8rem', lineHeight: 1.4, whiteSpace: 'pre-wrap', color: notification.read ? 'rgba(255,255,255,0.5)' : INK.soft }}>
                            {notification.body}
                          </Txt>
                        )}
                      </Box>
                    </ButtonBase>
                    <IconButton
                      aria-label="Delete notification"
                      onClick={(e) => handleDeleteNotification(notification._id, e)}
                      size="small"
                      sx={{
                        mt: 0.25, mr: 0.25, width: 40, height: 40,
                        color: 'rgba(255,255,255,0.35)',
                        '&:hover': { color: INK.red, background: 'transparent' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Snackbar for feedback messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </SwipeableDrawer>
  );
};

export default NotificationCenter;
